import { Modifier, convertFromRaw } from 'draft-js';
import {
  DEFAULT_FONT_SIZE, htmlToFormattedBlocks, MAX_LIST_DEPTH, ORDERED, pastePalette, UNORDERED,
} from '../../shared/paste/htmlPaste';

// Taille explicite égale à celle du bloc : pas de style posé, mais elle
// remplace la taille du curseur (cf. buildFragment).
const DEFAULT_SIZE = '__DEFAULT_FONT_SIZE__';

// Convertit le HTML du presse-papiers en fragment draft-js, ou null s'il n'y a
// rien d'exploitable (on retombe alors sur le collage en texte brut). Options :
// cf. pastePalette ; le nom de style draft-js d'une couleur est son label.
export const htmlToStyledFragment = (html, options = {}) => {
  const blocks = htmlToFormattedBlocks(html);
  if (!blocks) return null;
  const palette = pastePalette(options);

  // Un format est partagé par tout un morceau de texte : ses styles aussi.
  const stylesByFormat = new Map();
  const stylesOf = (format) => {
    if (!stylesByFormat.has(format)) {
      const style = [];
      if (format.bold) style.push('BOLD');
      if (format.italic) style.push('ITALIC');
      if (format.underline) style.push('UNDERLINE');
      const color = palette.color(format.color);
      if (color) style.push(color);
      const highlight = palette.highlight(format.highlight, format.highlightMarked);
      if (highlight) style.push(`HIGHLIGHT_${highlight}`);
      const size = palette.fontSize(format.fontSize);
      if (size) style.push(size === DEFAULT_FONT_SIZE ? DEFAULT_SIZE : `FONT_SIZE_${size}`);
      stylesByFormat.set(format, style);
    }
    return stylesByFormat.get(format);
  };

  return blocks.map(({ text, formats, list }) => ({ text, styles: formats.map(stylesOf), list }));
};

// « Conserver uniquement le texte » : le texte perd ses styles propres et prend
// ceux du curseur (passés en `baseStyles` à buildFragment) ; les listes de la
// source restent.
export const withoutOwnStyles = (blocks) => blocks.map((block) => ({
  ...block,
  styles: block.styles.map(() => []),
}));

// Deux fragments au même texte portent-ils les mêmes styles ? Sinon, le choix
// entre les deux collages a un sens.
const fragmentStyles = (fragment) => fragment
  .toList()
  .map((block) => block.getCharacterList().map((character) => character.getStyle().toSet()));
export const sameStyles = (a, b) => fragmentStyles(a).equals(fragmentStyles(b));

// Assemble les blocs pour Modifier.replaceWithFragment. `baseStyles` : styles
// du curseur conservés (taille, police…), sauf la taille là où le collage porte
// la sienne ; `blockType` et `depth` : ceux du bloc courant. Un paragraphe collé
// les prend, pour qu'un collage dans une liste reste au même niveau ; un élément
// de liste garde son type, son niveau s'ajoutant à celui de la liste courante.
export const buildFragment = (blocks, baseStyles, blockType, depth = 0) => {
  const inList = blockType === UNORDERED || blockType === ORDERED;
  const rawBlocks = blocks.map(({ text, styles: unitStyles, list }) => {
    // Les offsets bruts de draft-js comptent des points de code : un emoji
    // occupe deux unités de `text` mais une seule position.
    const styles = [];
    for (let i = 0; i < text.length; i += text.codePointAt(i) > 0xffff ? 2 : 1) {
      const own = unitStyles[i];
      const ownSize = own.some((style) => style === DEFAULT_SIZE || style.startsWith('FONT_SIZE_'));
      styles.push([
        ...baseStyles.filter((style) => !(ownSize && style.startsWith('FONT_SIZE_'))),
        ...own.filter((style) => style !== DEFAULT_SIZE),
      ]);
    }
    const inlineStyleRanges = [];
    const open = {};
    const close = (style, end) => {
      inlineStyleRanges.push({ style, offset: open[style], length: end - open[style] });
      delete open[style];
    };
    styles.forEach((charStyles, offset) => {
      Object.keys(open)
        .filter((style) => !charStyles.includes(style))
        .forEach((style) => close(style, offset));
      charStyles
        .filter((style) => !(style in open))
        .forEach((style) => { open[style] = offset; });
    });
    Object.keys(open).forEach((style) => close(style, styles.length));
    return {
      text,
      type: list ? list.type : blockType,
      depth: list ? Math.min(list.depth + (inList ? depth : 0), MAX_LIST_DEPTH) : depth,
      inlineStyleRanges,
      entityRanges: [],
    };
  });
  return convertFromRaw({ blocks: rawBlocks, entityMap: {} }).getBlockMap();
};

// Modifier.replaceWithFragment, sauf pour le type du bloc où l'on colle :
// draft-js change un paragraphe en liste quand on y colle un seul élément de
// liste, et garde le niveau du bloc quand le collage le remplace. Ici : texte
// avant le curseur, le bloc garde son type et son niveau ; sinon il prend ceux
// du premier bloc collé.
export const insertFragment = (content, selection, fragment) => {
  const target = content.getBlockForKey(selection.getStartKey());
  const source = selection.getStartOffset() > 0 ? target : fragment.first();
  const contentAfter = Modifier.replaceWithFragment(content, selection, fragment);
  const head = contentAfter.getBlockForKey(selection.getStartKey());
  return contentAfter.setIn(
    ['blockMap', head.getKey()],
    head.merge({ type: source.getType(), depth: source.getDepth() })
  );
};
